// frontend/src/components/NurseChecklistModal.jsx
import React, { useState } from 'react';
import api from '../services/api';

export default function NurseChecklistModal({ show, onClose, alert, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [pendencies, setPendencies] = useState({
        enxoval: false,
        higienizacao: false,
        montagem: false
    });

    if (!show || !alert) return null;

    const handleConfirm = async () => {
        if (!window.confirm('Confirmar que o leito está 100% apto para receber o paciente?')) return;

        setLoading(true);
        try {
            // 1. Atualizar alerta de enfermagem para concluído
            await api.patch(`/service-alerts/${alert.id}`, { status: 'completed' });

            // 2. Atualizar status do leito para "Pronto para Subida" (vamos definir um ID novo ou usar uma nota especial?)
            // O plano diz: "🚑 Reservado – pronto para subida"
            // Vamos manter status 10 (Reservado) mas atualizar a NOTA ou Metadata no backend se possível?
            // O backend serviceController não tem endpoint específico para "release reservation", mas updateStatus serve.
            // Vamos atualizar a nota para "Pronto para Subida". O status ID 10 permanece até o Check-in.

            await api.patch(`/beds/${alert.bed_id}/status`, {
                statusId: 10,
                notes: 'Pronto para Subida - Aprovado pela Enfermagem'
            });

            onSuccess();
            onClose();
        } catch (error) {
            console.error('Erro ao aprovar leito:', error);
            alert('Erro: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReportPendency = async () => {
        if (!pendencies.enxoval && !pendencies.higienizacao && !pendencies.montagem) {
            alert('Selecione pelo menos uma pendência.');
            return;
        }

        setLoading(true);
        try {
            // Reabrir cards específicos baseado nas pendências
            if (pendencies.enxoval) {
                await api.post('/service-alerts', {
                    service_type: 'laundry',
                    bed_id: alert.bed_id,
                    priority: 3,
                    status: 'pending',
                    metadata: JSON.stringify({ type: 'admission_kit', retry: true })
                });
            }
            if (pendencies.higienizacao) {
                await api.post('/service-alerts', {
                    service_type: 'cleaning',
                    bed_id: alert.bed_id,
                    priority: 3,
                    status: 'pending',
                    metadata: JSON.stringify({ type: 'terminal_cleaning', retry: true })
                });
            }

            // Logar que houve pendência e manter este card aberto (ou rependenciar?)
            // O usuário disse: "Abre modal ... Confirmar pendências -> Sistema reativa cards necessários... Mantém reserva ativa"
            // O card da enfermagem deve ficar aguardando novamente? Ou fechar e abrir outro depois?
            // "Sistema reativa os cards... Retorna para estado Reservado – aguardando preparação"

            // Vamos fechar este card de conferência (pois falhou) OU mantê-lo pendente?
            // Se reativar lavanderia, a lavanderia vai levar um tempo. A enfermagem só deve conferir DEPOIS que lavanderia acabar de novo.
            // Então logicamente devemos pausar ou remover este card da enfermagem e criar outro futuro, ou apenas deixá-lo lá?
            // O fluxo original diz: "Card Enfermagem... Conferência final".
            // Se falhou, volta passos trás.
            // Vou fechar este card com status "canceled" ou similar, ou apenas deixá-lo lá mas criar os outros.
            // Melhor: Deixar este card como está (pending), mas criar os outros. Quando os outros acabarem, eles não chamam "Check-out logic" de novo, então quem avisa a enfermagem?
            // O backend `updateServiceAlert` tem lógica: "Se lavanderia concluiu -> chama enfermagem".
            // Então, se eu criar um novo card de lavanderia, quando ele acabar, ele vai criar OUTRO card de enfermagem.
            // Então devo fechar este card atual (como 'skipped' ou 'failed') para não ficar duplicado.

            await api.patch(`/service-alerts/${alert.id}`, {
                status: 'completed', // Fechamos este ciclo
                metadata: JSON.stringify({ ...JSON.parse(alert.metadata || '{}'), outcome: 'failed', pendencies })
            });

            onSuccess();
            onClose();
        } catch (error) {
            console.error('Erro ao reportar pendências:', error);
            alert('Erro: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header bg-danger text-white">
                        <h5 className="modal-title"><i className="bi bi-clipboard-check me-2"></i>Conferência de Leito Reservado</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <p className="lead">O leito está pronto para receber o paciente?</p>

                        <div className="d-grid gap-2 mb-4">
                            <button className="btn btn-success btn-lg" onClick={handleConfirm} disabled={loading}>
                                <i className="bi bi-check-circle-fill me-2"></i>
                                SIM - Leito Apto
                            </button>
                        </div>

                        <hr />

                        <h6 className="text-danger">Não - Reportar Pendências:</h6>
                        <div className="card border-danger bg-light">
                            <div className="card-body">
                                <div className="form-check mb-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={pendencies.enxoval}
                                        onChange={e => setPendencies({ ...pendencies, enxoval: e.target.checked })}
                                        id="pendencia_enxoval"
                                    />
                                    <label className="form-check-label" htmlFor="pendencia_enxoval">Falta Enxoval (Acionar Lavanderia)</label>
                                </div>
                                <div className="form-check mb-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={pendencies.higienizacao}
                                        onChange={e => setPendencies({ ...pendencies, higienizacao: e.target.checked })}
                                        id="pendencia_higienizacao"
                                    />
                                    <label className="form-check-label" htmlFor="pendencia_higienizacao">Falta Higienização (Acionar Limpeza)</label>
                                </div>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={pendencies.montagem}
                                        onChange={e => setPendencies({ ...pendencies, montagem: e.target.checked })}
                                        id="pendencia_montagem"
                                    />
                                    <label className="form-check-label" htmlFor="pendencia_montagem">Falta Montagem/Equipamentos (Apenas Interno)</label>
                                </div>
                            </div>
                            <div className="card-footer bg-transparent border-top-0">
                                <button className="btn btn-outline-danger w-100" onClick={handleReportPendency} disabled={loading}>
                                    Confirmar Pendências e Solicitar Correção
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}
